/**
 * AuthGuard - Protezione route per PIGNA Studio
 * 
 * Verifica:
 * 1. Utente autenticato
 * 2. Ruolo enterprise o admin
 */

import { useAuth0 } from '@auth0/auth0-react';
import { hasStudioAccess, getUserRole } from '../auth/auth0-config';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { isLoading, isAuthenticated, loginWithRedirect, user, logout } = useAuth0();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
          <p className="text-xl font-semibold">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - Show login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="mb-4">
              <img 
                src="https://pigna.marinuzzi.it/backend/logo/oystar-2.jpg" 
                alt="OYSTAR Logo"
                className="h-20 mx-auto"
              />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-3">OYSTAR Flow</h1>
            <div className="text-gray-600 space-y-1">
              <p className="font-semibold">Visual AI Flow Editor</p>
              <p className="text-sm">RAG Enterprise</p>
              <p className="text-sm">Generazione Indici PDF e WEB</p>
              <p className="text-sm">Servizi API</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>🔐 Accesso Riservato</strong>
              </p>
              <p className="text-xs text-blue-600 mt-2">
                Solo utenti Enterprise possono accedere a OYSTAR Flow.
              </p>
            </div>
            
            <button
              onClick={() => loginWithRedirect()}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              🔑 Accedi con Auth0
            </button>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              Accedi con le stesse credenziali di oystar.ai
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated but not authorized (no enterprise role)
  if (!hasStudioAccess(user)) {
    const userRole = getUserRole(user);
    
    // DEBUG: Log completo dell'oggetto user per capire il problema
    console.log('🚫 AuthGuard: Accesso negato');
    console.log('  User completo:', JSON.stringify(user, null, 2));
    console.log('  Ruolo estratto:', userRole);
    console.log('  Namespace role:', user?.['https://oystar.ai/role']);
    console.log('  Direct role:', user?.role);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🚫</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Accesso Negato</h1>
            <p className="text-gray-600">OYSTAR Flow è riservato agli utenti Enterprise</p>
          </div>
          
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-orange-800 mb-2">
              <strong>Il tuo account:</strong>
            </p>
            <p className="text-xs text-orange-600">
              📧 {user?.email}<br/>
              🏷️ Ruolo: <strong>{userRole}</strong>
            </p>
            <p className="text-xs text-orange-500 mt-2">
              🔍 Debug: Apri la console (F12) per vedere i dettagli
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = 'https://oystar.ai'}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
              🏠 Vai a oystar.ai
            </button>
            
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
              🚪 Logout
            </button>
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-6">
            Per ottenere l'accesso a Studio, contatta il supporto per upgrade a Enterprise.
          </p>
        </div>
      </div>
    );
  }

  // Authenticated AND authorized - Show app!
  return <>{children}</>;
}

